import React, { useState, useRef, useEffect } from "react";
import { tr } from '@/i18n/tr';
import axios, { API } from "@/lib/api";
import { useDraggable } from "@/hooks/useDraggable";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

export default function AIButlerWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: tr('x_03a9825e') },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai-butler/chat`, { message: msg });
      setMessages(prev => [...prev, { role: "assistant", text: res.data.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", text: "⚠️ " + (e.response?.data?.detail || tr('x_b26b0699')) }]);
    }
    setLoading(false);
  };

  const suggestions = [tr('x_8b608503'), tr('x_0a0092b7'), tr('x_b23a6752'), tr('x_19a63387')];
  const drag = useDraggable("ai_butler_pos", () => ({ x: window.innerWidth - 72, y: window.innerHeight - 80 }), () => setOpen(true));

  return (
    <>
      {!open && (
        <button
          onPointerDown={drag.onPointerDown} style={drag.style}
          className="fixed w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 hover:scale-110 transition-transform shadow-xl shadow-emerald-900/40 z-50 flex items-center justify-center select-none"
          title={tr('x_9445221f')}
          data-testid="ai-butler-trigger"
        >
          <Sparkles className="w-6 h-6 text-white" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 right-2 md:bottom-6 md:right-6 w-[360px] max-w-[95vw] h-[520px] max-h-[70vh] md:max-h-[80vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 flex flex-col" data-testid="ai-butler-window">
          <div className="flex items-center justify-between p-3 border-b border-slate-700 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              <div>
                <div className="text-sm font-bold text-white">{tr('x_9445221f')}</div>
                <div className="text-[10px] text-slate-400">{tr('x_20448d20')}</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="ai-butler-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "bg-emerald-500 text-white rounded-br-sm" : "bg-slate-800 text-slate-200 rounded-bl-sm"}`}>{m.text}</div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="bg-slate-800 px-3 py-2 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-emerald-400" /></div></div>
            )}
            <div ref={endRef} />
          </div>
          {messages.length <= 2 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {suggestions.map(sg => (
                <button key={sg} onClick={() => setInput(sg)} className="text-[11px] px-2 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700" data-testid={`suggest-${sg}`}>{sg}</button>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-slate-700 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={tr('x_a6f1ba27')}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
              disabled={loading}
              data-testid="ai-butler-input"
            />
            <button onClick={send} disabled={loading || !input.trim()} className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 w-10 h-10 rounded-xl flex items-center justify-center" data-testid="ai-butler-send">
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
